import { useUserProfile } from '@/context/UserProfileContext';
import { useAppNavigation } from '@/lib/i18nNavigation';
import { IonLabel, IonRouterOutlet, IonTabBar, IonTabButton, IonTabs, useIonRouter } from '@ionic/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GolfAvatar } from './ui/GolfAvatar';
import GolfIcon from './GolfIcon';
import { GameSetup } from './game-setup';
import GolfRouter from './GolfRouter';
import GameTracker from './GameTracker';

export default function MobileNav() {
  const [showCaptions] = useState(true);
  const { profile } = useUserProfile();
  const { t } = useTranslation('MGP');
  const [showGameSetup, setShowGameSetup] = useState(false);
  const [pendingGameId, setPendingGameId] = useState<string | null>(null);
  const router = useIonRouter();

  const { getCurrentPathname } = useAppNavigation();
  const pathname = getCurrentPathname();

  // Handle pending game ID changes from GameTracker
  const handlePendingGameChange = (gameId: string | null) => {
    setPendingGameId(gameId);
  };

  // Only show tab bar on specific routes
  const shouldShowTabBar = (
    pathname === '/dashboard' ||
    pathname === '/' ||
    pathname === '/stats' ||
    pathname.startsWith('/explore') ||
    pathname.startsWith('/explore/') && !pathname.includes('/search') ||
    pathname.startsWith('/c/') && !pathname.includes('/edit') ||
    pathname === '/menu' ||
    pathname.startsWith('/u/') && !pathname.includes('/games')
  );

  if (!shouldShowTabBar) {
    // Return the router outlet without the tab bar for routes not in our allowlist
    return (
      <IonRouterOutlet>
        <GolfRouter hasProfile={!!profile} />
      </IonRouterOutlet>
    );
  }

  return (
    <>
      <IonTabs>
        <IonRouterOutlet>
          <GolfRouter hasProfile={!!profile} />
        </IonRouterOutlet>

        <IonTabBar color="transparent" slot="bottom" className="bg-card pt-1 border-t border-input md:hidden">
          <IonTabButton tab="dashboard" href="/dashboard" selected={pathname === "/dashboard" || pathname === "/"} className={`text-xs ${(pathname === "/dashboard" || pathname === "/") ? 'text-primary' : 'opacity-50'}`}>
            <GolfIcon themeAware={pathname !== "/dashboard" && pathname !== "/"} name={pathname === "/dashboard" || pathname === "/" ? "home-filled" : "home"} className="size-6" />
            {showCaptions && <IonLabel className="text-xs">{t('home')}</IonLabel>}
          </IonTabButton>

          {/* Search Tab Button */}
          <IonTabButton tab="search" href="/explore" selected={pathname.startsWith('/explore')} className={`text-xs ${(pathname.startsWith('/explore') ? 'text-primary' : 'opacity-50')}`}>
            <GolfIcon themeAware={pathname !== "/explore"} name={pathname.startsWith('/explore') ? "global-search-filled" : "global-search"} className="size-6" />
            {showCaptions && <IonLabel className="text-xs">{t('discover')}</IonLabel>}
          </IonTabButton>

          {/* Play Action Button - Shows Resume instead of Play when there are pending games */}
          <IonTabButton tab="action" onClick={() => pendingGameId ? router.push(`/play/${pendingGameId}`) : setShowGameSetup(true)} className='text-xs opacity-50'>
            <GolfIcon name="tee" className="size-6" />
            {showCaptions && <IonLabel className="text-xs">{pendingGameId ? t('resume') : t('play')}</IonLabel>}
          </IonTabButton>

          {/* Stats Tab Button */}
          <IonTabButton tab="stats" className={`text-xs ${(pathname === "/stats" ? 'text-primary' : 'opacity-50')}`} href="/stats" selected={pathname === "/stats"}>
            <GolfIcon themeAware={pathname !== "/stats"} name={pathname === "/stats" ? "analytics-filled" : "analytics"} className="size-6" />
            {showCaptions && <IonLabel className="text-xs">{t('insights')}</IonLabel>}
          </IonTabButton>

          {/* Profile Tab Button */}
          <IonTabButton
            tab="profile"
            href={profile ? `/u/${profile.username}` : '/welcome'}
            selected={pathname.includes(`/u/${profile?.username}`) || pathname === "/profile"}
          >
            <div className={`flex flex-col items-center justify-center rounded-full`}>
              <GolfAvatar className={`transition-all duration-300 ${pathname.includes(`/u/${profile?.username}`) || pathname === "/profile" ? 'border-2 border-primary bg-background' : ''}`} size={28} imageUrl={profile?.imageUrl} />
              {showCaptions && <IonLabel className={`text-xs ${(pathname.includes(`/u/${profile?.username}`) || pathname === "/profile") ? 'text-primary' : 'opacity-50'}`}>{t('profile')}</IonLabel>}
            </div>
          </IonTabButton>
        </IonTabBar>

        <GameSetup
          isOpen={showGameSetup}
          onClose={() => setShowGameSetup(false)}
        />
      </IonTabs>

      {/* Game tracker component to handle real-time game updates */}
      <GameTracker onPendingGameChange={handlePendingGameChange} />
    </>
  );
};
